<template>
  <!--
    页面级加载不挂根容器：本页首屏内容高度未撑满时，Element UI 的遮罩按容器高度居中，
    视觉上会贴在左上角（既有退货合同页同样不使用整页遮罩）。
    这里改为顶部「加载中」条 + 按钮级 loading，与既有退货合同「提交并前往盖章」的加载口径一致。
  -->
  <div class="sales-borrow-return">
    <div v-if="loading" class="page-loading-bar">
      <i class="el-icon-loading" /> 加载中…
    </div>
    <base-info v-model="form" :info="contractInfo" :category-options="categoryOptions" :disabled="!editable" />
    <history :approve-log-list="approvalLogs" />
    <!-- 附件信息：复用既有退货合同附件组件（打印/上传/下载/电子签章 + 文件列表）
         is-show=false 隐藏组件自带的「提交并返回列表 / 退回到待办」按钮，
         本流程的提交与流转由页面底部操作条统一控制。
         按原型该区块常驻显示；新建申请尚未落库时先拦截附件动作并落库，
         落库成功后重放该动作，避免附件挂到空单据上。 -->
    <div class="rj-page-card">
      <div class="rj-page-card__title">附件信息</div>
      <div ref="attachmentCard" class="rj-page-card__content" @click.capture="prepareAttachmentAction">
        <contract-file
          ref="attachFile"
          :contract-id="applicationId"
          :is-init="Boolean(applicationId)"
          :is-show="false"
        />
      </div>
    </div>
    <product
      :products="products"
      :available-products="availableProducts"
      :disabled="!editable"
      @remove="removeProduct"
      @add="addProduct"
    />
    <!-- 底部操作条：按原型吸底显示，按钮左对齐（与既有退货合同一致）。
         提交/召回/同意期间加载标记显示在按钮与操作条上，不使用整页遮罩 -->
    <div class="action-bar">
      <el-button v-if="editable" type="primary" :loading="actionLoading || saving" :disabled="saving" @click="submit">提交</el-button>
      <el-button v-if="canRecall" :loading="actionLoading" @click="recall">召回</el-button>
      <el-button v-if="canApprove" type="primary" :loading="actionLoading" @click="approve">同意</el-button>
      <el-button v-if="canApprove" type="danger" :disabled="actionLoading" @click="reject">驳回</el-button>
      <span v-if="actionLoading || saving" class="action-tip"><i class="el-icon-loading" /> 处理中…</span>
    </div>
  </div>
</template>

<script>
import baseInfo from './baseInfo.vue';
import history from './history.vue';
import product from './product.vue';
// 与既有退货合同办理页共用附件组件，保持上传、下载、打印和电子签章口径一致
import contractFile from '@/views/returnExchange/Chief/commom/file.vue';
import {
  previewSalesBorrowServiceReturn, createSalesBorrowServiceReturn,
  updateSalesBorrowServiceReturn, getSalesBorrowServiceReturn,
  submitSalesBorrowServiceReturn, recallSalesBorrowServiceReturn,
  approveSalesBorrowServiceReturn, rejectSalesBorrowServiceReturn
} from '@/API';

export default {
  name: 'SalesBorrowPureServiceReturn',
  components: { baseInfo, history, product, contractFile },
  data () {
    return {
      loading: false,
      saving: false,
      actionLoading: false,
      // 附件动作拦截中标记，避免落库成功后重放点击再次进入拦截逻辑
      persistingForAttachment: false,
      applicationId: this.$route.query.applicationId || '',
      originalContractId: Number(this.$route.params.contractId || this.$route.query.contractId),
      contractInfo: {},
      categoryOptions: [],
      approvalLogs: [],
      products: [],
      // 数仓返回的全部可退服务行，供编辑态「添加产品」挑选（不直接展示）
      availableProducts: [],
      form: { scenarioCategoryKey: '', scenarioCategoryValue: '', supplementalTerm: '', remarks: '' },
      application: null
    };
  },
  computed: {
    currentUserId () { return window.$core && window.$core.user && window.$core.user.userId; },
    // 本流程使用专用状态区间：20=草稿 21=待经理审批 22=ERP处理中 23=已完成
    // （不再复用通用合同状态 0/3/4，避免与订单员、签章、财务节点语义冲突）
    // 展示口径：列表文案由后端统一投影，22/23 均显示「商务大区经理已审批」
    // （用户当前决策 2026-10-06，不向商务暴露 ERP 阶段）
    statusCode () { return this.application ? Number(this.application.rcontractStatus) : null; },
    editable () {
      if (!this.application) return true;
      const assistantCode = this.application.rassistantCode || this.application.rAssistantCode;
      return this.statusCode === 20 && String(this.currentUserId || '') === String(assistantCode || '');
    },
    waiting () { return this.statusCode === 21; },
    canRecall () {
      const assistantCode = this.application && (this.application.rassistantCode || this.application.rAssistantCode);
      return this.waiting && String(this.currentUserId || '') === String(assistantCode || '');
    },
    canApprove () {
      return this.waiting && String(this.currentUserId || '') === String(this.application.rbusinessAreaManagerId || this.application.rBusinessAreaManagerId || '');
    }
  },
  mounted () { this.applicationId ? this.loadDetail() : this.loadPreview(); },
  methods: {
    async loadPreview () {
      this.loading = true;
      try {
        const preview = await previewSalesBorrowServiceReturn({ originalContractId: this.originalContractId });
        if (!this.requireSuccess(preview)) return;
        // 草稿尚未生成退货明细，净销售额取预览阶段的服务行拆分前金额汇总
        this.contractInfo = { ...(preview.data.data.contract || {}), netSales: preview.data.data.netSales };
        this.categoryOptions = preview.data.data.categoryOptions || [];
        this.products = (preview.data.data.products || []).map(this.normalizeProduct);
        // 新建态同样要保留全量可退行作为「添加产品」候选：
        // 页面只展示已选行，用户删掉的行仍需能从弹窗重新挑回（与编辑态口径一致）
        this.availableProducts = this.products.map(item => ({ ...item }));
        if (!this.products.length) this.$message.error('原合同没有可退服务产品');
      } finally { this.loading = false; }
    },
    async loadDetail () {
      this.loading = true;
      try {
        const response = await getSalesBorrowServiceReturn(this.applicationId);
        if (!this.requireSuccess(response)) return;
        const data = response.data.data;
        this.originalContractId = data.originalContractId;
        this.application = data.application;
        // 草稿由提交前落库生成后才会拿到申请单主键，这里补写路由 query 供附件组件读取
        await this.syncAttachmentRoute();
        // 合并顺序：原合基础信息 -> 退货明细（净销售额/返利退还金额）-> 申请单自身字段
        this.contractInfo = { ...(data.contract || {}), ...(data.returnDetail || {}), ...data.application };
        this.products = (data.products || []).map(this.normalizeProduct);
        this.approvalLogs = data.approvalLogs || [];
        this.form = {
          scenarioCategoryKey: data.application.rscenarioCategoryKey,
          scenarioCategoryValue: data.application.rscenarioCategoryValue,
          supplementalTerm: data.application.rsupplementalTerm,
          remarks: data.application.rremarks
        };
        if (this.editable) {
          const preview = await previewSalesBorrowServiceReturn({ originalContractId: this.originalContractId });
          if (this.requireSuccess(preview)) {
            this.categoryOptions = preview.data.data.categoryOptions || [];
            // 编辑态保留全量可退行作为「添加产品」候选：
            // 页面只展示已落库的已选行，新增由用户从候选中挑，对齐 ECP 的可退行判定
            this.availableProducts = (preview.data.data.products || []).map(this.normalizeProduct);
          }
        }
      } finally { this.loading = false; }
    },
    requestData () {
      return {
        originalContractId: this.originalContractId,
        ...this.form,
        // 产品清单来自数仓，行唯一标识为 ERP 行 ID（对齐 ECP）；
        // 型号与单价由服务端按数仓行重建，前端回传行标识、库位与退货数量
        products: this.products.map(item => ({
          erpLineId: item.erpLineId,
          erpLineNum: item.erpLineNum,
          erpOrderNum: item.erpOrderNum,
          returnQuantity: item.productNum,
          storageName: item.storageName
        }))
      };
    },
    validate (requireStorage = false) {
      if (!this.form.scenarioCategoryKey) {
        this.$message.error('请选择场景分类');
        return false;
      }
      if (!this.products.length) {
        this.$message.error('至少保留一条服务产品');
        return false;
      }
      // 退货数量前端先兜一道：后端仍会按数仓原单数量再校验一次，避免被绕过。
      // 销售借用纯服务退货按整数退货（用户当前决策 2026-10-06）。
      const invalid = this.products.find(item => {
        const quantity = Number(item.productNum);
        const original = Number(item.originalQuantity);
        return !Number.isInteger(quantity) || quantity <= 0 || (original && quantity > original);
      });
      if (invalid) {
        this.$message.error(`退货数量必须为大于 0 的整数且不超过原单数量（${invalid.productName || '服务产品'}）`);
        return false;
      }
      if (requireStorage) {
        // 库位必填范围对齐既有退货合同：仅「已发货」行强制填写，未发货行允许留空。
        // deliverSign 由服务端按数仓发货量计算并随产品行返回。
        const missing = this.products.find(item => Number(item.deliverSign) === 1 &&
          !String(item.storageName || '').trim());
        if (missing) {
          this.$message.error(`请填写已发货服务产品的库位（${missing.productName || '服务产品'}）`);
          return false;
        }
      }
      return true;
    },
    /**
     * 落库申请数据，作为提交的前置步骤。
     * <p>
     * PRD 2.7 与原型只提供「提交申请」，不提供独立保存草稿入口；
     * 本步骤仅用于提交前创建或更新申请记录，成功后立即继续提交，
     * 失败则中断并不进入提交流程。
     * </p>
     *
     * @returns {Promise<boolean>} 是否落库成功
     */
    async persistApplication () {
      if (!this.validate()) return false;
      this.saving = true;
      try {
        const response = this.applicationId
          ? await updateSalesBorrowServiceReturn(this.applicationId, this.requestData())
          : await createSalesBorrowServiceReturn(this.requestData());
        if (!this.requireSuccess(response)) return false;
        if (!this.applicationId) this.applicationId = response.data.data;
        await this.loadDetail();
        // 首次落库后附件组件才能按真实单据查询，这里主动刷新一次列表
        await this.$nextTick();
        const attachFile = this.$refs.attachFile;
        if (attachFile && typeof attachFile.searchFiles === 'function') attachFile.searchFiles();
        return true;
      } finally { this.saving = false; }
    },
    async submit () {
      if (!this.validate(true)) return;
      if (!await this.persistApplication()) return;
      // 提交（发布）后返回退换货合同查询列表
      await this.action(submitSalesBorrowServiceReturn, {}, '提交成功', 'assistant');
    },
    /**
     * 附件动作前置拦截。
     * <p>
     * 附件按单据主键（申请单 r_id）挂载，而新建申请在提交前尚未落库，
     * 此时直接执行上传/打印/签章会把附件写到空单据上。
     * 这里先阻断原动作，落库并回写附件所需的单据主键后再重放点击。
     * </p>
     * <p>
     * 注意：浏览器对文件选择框、新开窗口有瞬时用户激活限制（Chrome 默认 5 秒），
     * 落库耗时较长时重放可能被静默拦截。因此重放仅作为便利优化，
     * 同时明确提示申请已保存；单据已落库后再次点击即可直接执行，不会重复落库。
     * </p>
     *
     * @param {Event} event 附件区域内的点击事件
     */
    async prepareAttachmentAction (event) {
      if (this.applicationId || this.persistingForAttachment) return;
      const button = event.target && event.target.closest ? event.target.closest('button') : null;
      if (!button) return;
      // 捕获阶段阻断，避免附件组件在空单据上执行原动作
      event.stopPropagation();
      const label = (button.textContent || '').trim();
      this.$message.info('首次操作附件需先保存申请，正在保存…');
      if (!await this.persistApplication()) return;
      this.$message.success('申请已保存');
      await this.$nextTick();
      const card = this.$refs.attachmentCard;
      const replay = card && Array.from(card.querySelectorAll('button'))
        .find(item => (item.textContent || '').trim() === label);
      if (!replay) return;
      this.persistingForAttachment = true;
      try { replay.click(); } finally { this.persistingForAttachment = false; }
    },
    recall () {
      this.$confirm('是否确认召回该销售借用纯服务退货申请？', '提示', {
        confirmButtonText: '召回',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => this.action(recallSalesBorrowServiceReturn, { opinion: '申请人召回' }, '召回成功')).catch(() => {});
    },
    approve () { this.askOpinion('同意', false, approveSalesBorrowServiceReturn, 'manager'); },
    reject () { this.askOpinion('驳回', true, rejectSalesBorrowServiceReturn, 'manager'); },
    /**
     * 弹出审批意见输入框并提交审批。
     *
     * @param {string} title 动作名称，直接作为提示文案前缀（同意/驳回）
     * @param {boolean} required 意见是否必填
     * @param {Function} api 审批接口
     * @param {string} returnTarget 完成后跳转的目标列表标识
     */
    askOpinion (title, required, api, returnTarget = '') {
      this.$prompt('请输入审批意见', title, { inputValidator: value => !required || Boolean(value) || '驳回意见不能为空' })
        .then(({ value }) => this.action(api, { opinion: value }, `${title}成功`, returnTarget)).catch(() => {});
    },
    /**
     * 执行提交/审批动作。
     *
     * @param {Function} api 接口
     * @param {object} body 请求体
     * @param {string} success 成功提示文案
     * @param {string} returnTarget 完成后跳转目标：assistant=退换货合同查询，manager=审批过的退换货合同，空=留在本页刷新
     */
    async action (api, body, success, returnTarget = '') {
      if (this.actionLoading) return;
      this.actionLoading = true;
      try {
        const response = await api(this.applicationId, body);
        if (!this.requireSuccess(response)) return;
        this.$message.success(success);
        if (returnTarget) {
          this.returnToList(returnTarget);
          return;
        }
        await this.loadDetail();
      } finally {
        this.actionLoading = false;
      }
    },
    /**
     * 跳转到对应列表页。
     * <p>
     * 不同场景跳转的列表不同：
     * </p>
     * <ul>
     *     <li>{@code assistant}：商务提交后回「退换货合同查询」列表（第 6 个页签），
     *     由 vuex {@code pagesTemporary.AssistantContractList} 控制激活；</li>
     *     <li>{@code manager}：商务大区经理审批后回「合同处理 - 审批过的退换货合同」
     *     （第 7 个页签），由 vuex {@code pagesTemporary.OrderPersionContractListTabActive} 控制激活。
     *     该列表已按经理身份过滤本流程单据（后端 {@code salesBorrowManagerOnly} 分支），
     *     与 PRD「商务大区经理审批列表：合同处理的待审批/已审批退换货合同两个页签」一致。</li>
     * </ul>
     * <p>
     * 不使用 {@code $router.back()}：申请页可能由列表弹窗 push 打开或在新标签页直接打开，
     * 回退目标不稳定；统一显式跳转并设置对应页签，否则会停在该列表的默认页签。
     * </p>
     *
     * @param {string} target 目标列表标识：assistant / manager
     */
    returnToList (target) {
      if (target === 'manager') {
        // 审批过的退换货合同
        this.$store.commit('pagesTemporary/SETOrderPersionContractListTabActive', '7');
        this.$router.push({ name: 'orderPersionContractList' });
        return;
      }
      // 退换货合同查询
      this.$store.commit('pagesTemporary/SETassistantContractList', '6');
      this.$router.push({ name: 'assistantContractList' });
    },
    /**
     * 同步附件组件所需的单据主键到路由 query。
     * <p>
     * 附件组件（既有退货合同共用）统一优先从 {@code $route.query.contractId} 取当前单据主键，
     * 首次提交落库后才生成申请单主键，此时需回写 query，否则附件列表与上传会指向空单据。
     * 回写为异步路由替换，必须等其完成后再触发附件动作，否则组件仍会读到旧值。
     * </p>
     *
     * @returns {Promise<void>} 路由回写完成
     */
    async syncAttachmentRoute () {
      if (!this.applicationId) return;
      if (String(this.$route.query.contractId || '') === String(this.applicationId)) return;
      await this.$router.replace({
        ...this.$route,
        query: { ...this.$route.query, applicationId: this.applicationId, contractId: this.applicationId }
      }).catch(() => {});
      await this.$nextTick();
    },
    removeProduct (index) { this.products.splice(index, 1); },
    /**
     * 从「添加产品」候选集中新增产品行。
     *
     * 保留用户已填的库位不影响新增行；新增行库位为空，由用户自行填写。
     *
     * @param {object} row 候选产品行
     */
    addProduct (row) {
      if (this.products.some(item => String(item.erpLineId) === String(row.erpLineId))) return;
      this.products.push(row);
      this.$message.success('已添加产品');
    },
    normalizeProduct (item) {
      // 后端 ReContractProduct 经 Jackson 序列化后，getRProductCode() 生成的是
      // rproductCode（r 之后首字母小写），与项目其他页面一致；
      // 这里同时兼容 rproductCode 与 rProductCode 两种写法，避免字段全空
      const pick = (lower, upper, fallback) => {
        if (item[lower] !== undefined && item[lower] !== null) return item[lower];
        if (item[upper] !== undefined && item[upper] !== null) return item[upper];
        return fallback;
      };
      return {
        ...item,
        erpLineId: item.erpLineId,
        erpLineNum: item.erpLineNum,
        erpOrderNum: item.erpOrderNum,
        productCode: pick('rproductCode', 'rProductCode', item.productCode),
        productName: pick('rproductName', 'rProductName', item.productName),
        serviceLife: pick('rserviceLength', 'rServiceLength', item.serviceLife),
        productNum: pick('rproductNum', 'rProductNum', item.productNum),
        // 原单数量：数量编辑的上限，同时用于按比例换算本行合计金额
        originalQuantity: pick('roriginalQuantity', 'rOriginalQuantity', item.originalQuantity),
        materialNum: pick('rmaterialNum', 'rMaterialNum', item.materialNum),
        productUnit: pick('rproductUnit', 'rProductUnit', item.productUnit),
        productPrice: pick('rproductPrice', 'rProductPrice', item.productPrice),
        productPriceAdd: pick('rproductPriceAdd', 'rProductPriceAdd', item.productPriceAdd),
        productActualPriceAdd: pick('rproductActualPriceAdd', 'rProductActualPriceAdd', item.productActualPriceAdd),
        beforeSplitPrice: pick('rbeforeSplitPrice', 'rBeforeSplitPrice', item.beforeSplitPrice),
        fictitiousReturn: pick('rfictitiousReturn', 'rFictitiousReturn', item.fictitiousReturn),
        storageName: pick('rstorageName', 'rStorageName', item.storageName),
        // 发货标识：1 已发货、0 未发货，由服务端按数仓发货量计算。
        // 库位必填范围对齐既有退货合同——仅已发货行必须填写
        deliverSign: pick('rdeliverSign', 'rDeliverSign', item.deliverSign),
        taxRate: item.taxRate
      };
    },
    /**
     * 判断接口是否成功。
     * <p>
     * 失败提示统一由全局 axios 响应拦截器给出；这里只负责放行/阻断后续流程，
     * 仅在响应不带任何错误文案（拦截器不会提示）时兜底提示一次，
     * 避免同一次失败出现两条重复提示。
     * </p>
     *
     * @param {object} response 接口响应
     * @returns {boolean} 是否成功
     */
    requireSuccess (response) {
      const body = response && response.data;
      if (body && body.status === '200') return true;
      const notified = body && (body.error || body.err || body.msg || body.message);
      if (!notified) this.$message.error('操作失败');
      return false;
    }
  }
};
</script>

<style scoped>
/*
 * 底部操作条对齐原型：固定在内容区底部，按钮左对齐（原实现为右对齐）。
 * 用 sticky 而非 fixed：本页处于带侧边栏的内容区，fixed + left:0 会覆盖左侧菜单；
 * sticky 在滚动容器内吸底，视觉与原型一致且不影响布局。
 */
.action-bar {
  position: sticky;
  bottom: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 11px 20px;
  background: #fff;
  border-top: 1px solid #e4e7ed;
  box-shadow: 0 -4px 12px rgba(0, 0, 0, .04);
}
/*
 * 页面级加载提示。
 * 原先用 v-loading 挂根容器，首屏容器高度未撑满时遮罩会贴在左上角；
 * 改为条状提示放在内容顶部，位置稳定且不遮挡页面。
 */
.page-loading-bar {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  margin-bottom: 10px;
  color: #409eff;
  font-size: 13px;
  background: #ecf5ff;
  border: 1px solid #d9ecff;
  border-radius: 4px;
}
/* 底部操作条内的进行中提示，与按钮 loading 一起反馈，避免整页遮罩 */
.action-tip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #909399;
  font-size: 13px;
}
</style>
